import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const translateBackend = (t, payload, fallbackKey = "msg_server_or_network") => {
  if (!payload) return t(fallbackKey);
  if (payload.message_key) return t(payload.message_key, payload.message_params || {});
  if (payload.message) return String(payload.message);
  return t("msg_unknown_error");
};

const emptyMsg = { type: "", text: "" };

const QuestionForm = () => {
  const { t } = useTranslation();
  const API = process.env.REACT_APP_API_URL;

  const [application, setApplication] = useState("");

  const [machineTypes, setMachineTypes] = useState([]);
  const [questions, setQuestions] = useState([]);

  const [newMachineType, setNewMachineType] = useState("");
  const [editingMachineType, setEditingMachineType] = useState(null);
  const [editMachineTypeName, setEditMachineTypeName] = useState("");

  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [selected, setSelected] = useState(new Set());

  const [questionForm, setQuestionForm] = useState({
    machineTypeId: "",
    text: "",
    type: "open", // open | mcq | yes_no
    required: false,
    options: "",
    interventionType: "repair_report", // repair_report | ask_repair_intervention
  });

  const [typeMsg, setTypeMsg] = useState(emptyMsg);
  const [questionMsg, setQuestionMsg] = useState(emptyMsg);
  const [listMsg, setListMsg] = useState(emptyMsg);

  const msgClass = (msg) =>
    msg.type === "error"
      ? "rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      : "rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success";

  const inputClass =
    "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20";

  useEffect(() => {
    const storedApplication = localStorage.getItem("userApplication") || "";
    setApplication(storedApplication);
  }, []);

  useEffect(() => {
    if (!application.trim()) return;
    fetchMachineTypes();
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application]);

  const normalizeMachineTypes = (rawList) =>
    (rawList || [])
      .map((m) => ({
        id: String(m?.id ?? ""),
        name: m?.name || m?.type || "",
      }))
      .filter((m) => m.id && m.name);

  const fetchMachineTypes = async () => {
    setTypeMsg(emptyMsg);
    try {
      const res = await axios.get(`${API}/api/machine_types`, { params: { application } });
      const list =
        (Array.isArray(res.data?.data) && res.data?.data) ||
        (Array.isArray(res.data?.data?.machine_types) && res.data?.data?.machine_types) ||
        (Array.isArray(res.data?.machine_types) && res.data?.machine_types) ||
        (Array.isArray(res.data) && res.data) ||
        [];

      setMachineTypes(normalizeMachineTypes(list));
    } catch (e) {
      setTypeMsg({ type: "error", text: translateBackend(t, e?.response?.data, "qf_err_load_types") });
      setMachineTypes([]);
    }
  };

  const fetchQuestions = async () => {
    setListMsg(emptyMsg);
    try {
      const res = await axios.get(`${API}/api/questions`, { params: { application } });
      const list =
        (Array.isArray(res.data?.data) && res.data?.data) ||
        (Array.isArray(res.data?.data?.questions) && res.data?.data?.questions) ||
        (Array.isArray(res.data?.questions) && res.data?.questions) ||
        (Array.isArray(res.data) && res.data) ||
        [];

      setQuestions(list);
      setSelected(new Set());
    } catch (e) {
      setListMsg({ type: "error", text: translateBackend(t, e?.response?.data, "qf_err_load_questions") });
    }
  };

  const addMachineType = async () => {
    const name = newMachineType.trim();
    if (!name) return;
    if (!application.trim()) {
      setTypeMsg({ type: "error", text: t("qf_app_missing") });
      return;
    }

    setTypeMsg(emptyMsg);
    try {
      const res = await axios.post(`${API}/api/machine_types`, {
        type: name,
        application,
        application_name: application,
      });

      setTypeMsg({ type: "success", text: translateBackend(t, res.data, "qf_type_added") });
      setNewMachineType("");
      await fetchMachineTypes();
    } catch (e) {
      setTypeMsg({ type: "error", text: translateBackend(t, e?.response?.data, "qf_type_add_failed") });
    }
  };

  const saveMachineType = async (id) => {
    const name = editMachineTypeName.trim();
    if (!name) return;
    if (!application.trim()) {
      setTypeMsg({ type: "error", text: t("qf_app_missing") });
      return;
    }

    setTypeMsg(emptyMsg);
    try {
      const res = await axios.put(`${API}/api/machine_types/${id}`, { type: name, application });
      setTypeMsg({ type: "success", text: translateBackend(t, res.data, "qf_type_updated") });
      setEditingMachineType(null);
      setEditMachineTypeName("");
      await fetchMachineTypes();
      await fetchQuestions();
    } catch (e) {
      setTypeMsg({ type: "error", text: translateBackend(t, e?.response?.data, "qf_type_update_failed") });
    }
  };

  const removeMachineType = async (id) => {
    if (!window.confirm(t("qf_confirm_delete_type"))) return;
    if (!application.trim()) {
      setTypeMsg({ type: "error", text: t("qf_app_missing") });
      return;
    }

    setTypeMsg(emptyMsg);
    try {
      const res = await axios.delete(`${API}/api/machine_types/${id}`, { params: { application } });
      setTypeMsg({ type: "success", text: translateBackend(t, res.data, "qf_type_deleted") });
      await fetchMachineTypes();
      await fetchQuestions();
    } catch (e) {
      setTypeMsg({ type: "error", text: translateBackend(t, e?.response?.data, "qf_type_delete_failed") });
    }
  };

  const addQuestion = async () => {
    const text = questionForm.text.trim();
    if (!text || !questionForm.machineTypeId) return;
    if (!application.trim()) {
      setQuestionMsg({ type: "error", text: t("qf_app_missing") });
      return;
    }

    const mappedType = questionForm.type === "mcq" ? "qcm" : questionForm.type;
    const options = mappedType === "qcm"
      ? String(questionForm.options || "").split(",").map((o) => o.trim()).filter(Boolean)
      : [];

    if (mappedType === "qcm" && options.length === 0) {
      setQuestionMsg({ type: "error", text: t("qf_qcm_need_options") });
      return;
    }

    setQuestionMsg(emptyMsg);
    try {
      const res = await axios.post(`${API}/api/questions`, {
        text,
        application,
        intervention_type: questionForm.interventionType,
        machine_type_id: Number(questionForm.machineTypeId),
        question_type: mappedType,
        is_required: !!questionForm.required,
        options,
      });

      setQuestionMsg({ type: "success", text: translateBackend(t, res.data, "qf_question_added") });
      setQuestionForm({
        machineTypeId: "",
        text: "",
        type: "open",
        required: false,
        options: "",
        interventionType: "repair_report",
      });
      setShowAddQuestion(false);
      await fetchQuestions();
    } catch (e) {
      setQuestionMsg({ type: "error", text: translateBackend(t, e?.response?.data, "qf_question_add_failed") });
    }
  };

  const deleteQuestion = async (id) => {
    setListMsg(emptyMsg);
    try {
      const res = await axios.delete(`${API}/api/delete_question/${id}`);
      setListMsg({ type: "success", text: translateBackend(t, res.data, "qf_question_deleted") });
      await fetchQuestions();
    } catch (e) {
      setListMsg({ type: "error", text: translateBackend(t, e?.response?.data, "qf_question_delete_failed") });
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;

    setListMsg(emptyMsg);
    try {
      for (const id of ids) {
        await axios.delete(`${API}/api/delete_question/${id}`);
      }
      setSelected(new Set());
      setListMsg({ type: "success", text: t("qf_delete_selected_success", { count: ids.length }) });
      await fetchQuestions();
    } catch (e) {
      setListMsg({ type: "error", text: translateBackend(t, e?.response?.data, "qf_delete_selected_failed") });
    }
  };

  const filteredQuestions = useMemo(() => {
    if (filterType === "all") return questions;
    return questions.filter((q) => (q.intervention_type || "repair_report") === filterType);
  }, [questions, filterType]);

  const allVisibleSelected = filteredQuestions.length > 0 && filteredQuestions.every((q) => selected.has(String(q.id)));

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const machineNameById = (id) => machineTypes.find((m) => String(m.id) === String(id))?.name || "—";

  const questionTypeLabel = (type) => {
    if (type === "open") return t("qf_type_open");
    if (type === "qcm" || type === "mcq") return t("qf_type_qcm");
    return t("qf_type_yesno");
  };

  const interventionLabel = (type) =>
    type === "ask_repair_intervention" ? t("qf_it_ask_repair") : t("qf_it_report_repair");

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-foreground">{t("qf_questions_list_title", "Gestion des questions")}</h1>

      <div className="mt-6 rounded-xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold text-foreground">{t("qf_machine_types_title", "Types de machine")}</h2>

        {typeMsg.text && <p className={`mt-4 ${msgClass(typeMsg)}`}>{typeMsg.text}</p>}

        <div className="mt-4 flex gap-2">
          <input
            type="text"
            value={newMachineType}
            onChange={(e) => setNewMachineType(e.target.value)}
            className={inputClass}
            placeholder={t("qf_add_type_ph", "Nouveau type de machine...")}
          />
          <button onClick={addMachineType} className="shrink-0 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            {t("add", "Ajouter")}
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {machineTypes.map((mt) => (
            <div key={mt.id} className="flex items-center gap-1 rounded-lg border border-border bg-muted px-3 py-1.5">
              {editingMachineType === mt.id ? (
                <>
                  <input
                    type="text"
                    value={editMachineTypeName}
                    onChange={(e) => setEditMachineTypeName(e.target.value)}
                    className="w-28 rounded border border-input bg-background px-2 py-0.5 text-xs"
                  />
                  <button onClick={() => saveMachineType(mt.id)} className="text-xs text-success">✓</button>
                  <button onClick={() => { setEditingMachineType(null); setEditMachineTypeName(""); }} className="text-xs text-muted-foreground">×</button>
                </>
              ) : (
                <>
                  <span className="text-sm text-foreground">{mt.name}</span>
                  <button onClick={() => { setEditingMachineType(mt.id); setEditMachineTypeName(mt.name); }} className="text-xs text-primary">✎</button>
                  <button onClick={() => removeMachineType(mt.id)} className="text-xs text-destructive">×</button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-foreground">{t("qf_add_question_title", "Questions")}</h2>

          <div className="flex gap-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              <option value="all">{t("qf_filter_all")}</option>
              <option value="repair_report">{t("qf_it_report_repair")}</option>
              <option value="ask_repair_intervention">{t("qf_it_ask_repair")}</option>
            </select>

            <button onClick={() => setShowAddQuestion(true)} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
              {t("add", "Ajouter")}
            </button>

            {selected.size > 0 && (
              <button
                onClick={deleteSelected}
                className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground"
              >
                {t("delete", "Supprimer")} ({selected.size})
              </button>
            )}
          </div>
        </div>

        {questionMsg.text && <p className={`mt-4 ${msgClass(questionMsg)}`}>{questionMsg.text}</p>}
        {listMsg.text && <p className={`mt-4 ${msgClass(listMsg)}`}>{listMsg.text}</p>}

        <div className="mt-4 overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-8 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected(new Set(filteredQuestions.map((q) => String(q.id))));
                      } else {
                        setSelected(new Set());
                      }
                    }}
                  />
                </th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("qf_col_question")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("qf_col_type")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("qf_col_machine")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("qf_col_intervention")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("actions", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuestions.map((q) => (
                <tr key={q.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(String(q.id))}
                      onChange={() => toggleSelect(q.id)}
                    />
                  </td>
                  <td className="px-4 py-3 text-foreground">
                    {q.text} {!!q.is_required && <span className="text-destructive">*</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {questionTypeLabel(q.question_type || q.type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{machineNameById(q.machine_type_id || q.machineTypeId)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{interventionLabel(q.intervention_type || q.interventionType)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => deleteQuestion(q.id)}
                      className="text-xs text-destructive hover:underline"
                    >
                      {t("delete", "Supprimer")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAddQuestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-foreground">{t("qf_add_question_title", "Ajouter une question")}</h3>

            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t("qf_machine_types_title", "Type de machine")}</label>
                <select
                  value={questionForm.machineTypeId}
                  onChange={(e) => setQuestionForm((f) => ({ ...f, machineTypeId: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">{t("qf_select_machine_type_placeholder", "Sélectionner...")}</option>
                  {machineTypes.map((mt) => (
                    <option key={mt.id} value={mt.id}>{mt.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t("qf_question_ph", "Question")}</label>
                <input
                  type="text"
                  value={questionForm.text}
                  onChange={(e) => setQuestionForm((f) => ({ ...f, text: e.target.value }))}
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t("qf_type_open", "Type")}</label>
                  <select
                    value={questionForm.type}
                    onChange={(e) => setQuestionForm((f) => ({ ...f, type: e.target.value }))}
                    className={inputClass}
                  >
                    <option value="open">{t("qf_type_open")}</option>
                    <option value="mcq">{t("qf_type_qcm")}</option>
                    <option value="yes_no">{t("qf_type_yesno")}</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t("qf_it_report_repair", "Intervention")}</label>
                  <select
                    value={questionForm.interventionType}
                    onChange={(e) => setQuestionForm((f) => ({ ...f, interventionType: e.target.value }))}
                    className={inputClass}
                  >
                    <option value="repair_report">{t("qf_it_report_repair")}</option>
                    <option value="ask_repair_intervention">{t("qf_it_ask_repair")}</option>
                  </select>
                </div>
              </div>

              {questionForm.type === "mcq" && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t("qf_options", "Options")}</label>
                  <input
                    type="text"
                    value={questionForm.options}
                    onChange={(e) => setQuestionForm((f) => ({ ...f, options: e.target.value }))}
                    className={inputClass}
                    placeholder={t("qf_options_placeholder")}
                  />
                </div>
              )}

              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={questionForm.required}
                  onChange={(e) => setQuestionForm((f) => ({ ...f, required: e.target.checked }))}
                  className="rounded border-input"
                />
                {t("required", "Obligatoire")}
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowAddQuestion(false)} className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground">
                {t("cancel", "Annuler")}
              </button>
              <button onClick={addQuestion} className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">
                {t("save", "Enregistrer")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionForm;
